import React, { useState } from "react";
import { Edit3, LogIn, LogOut, X as CancelIcon, Calendar, Clock, MapPin, Users } from "lucide-react";
import AddonsBreakdown from "../../../components/addons/AddonsBreakdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../shadcn/ui/dialog";
import { canModify, canCheckIn, canCheckOut, canCancel } from "../../../utils/bookingActionsUtils";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";


const BookingActionsModal = ({
  isOpen,
  onClose,
  booking,
  onModify,
  onCancel,
  onCheckIn,
  onCheckOut,
  formatTimeRange,
  showConfirmation,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Get booking status for display
  const getBookingStatus = () => {
    if (!booking.status) return "pending";
    return booking.status.toLowerCase();
  };

  const getStatusColor = () => {
    const status = getBookingStatus();
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "checked_in":
      case "active":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
      case "canceled":
      case "no_show":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = () => {
    const status = getBookingStatus();
    switch (status) {
      case "checked_in":
      case "active":
        return <LogIn className="w-3 h-3" />;
      case "pending":
        return <Clock className="w-3 h-3" />;
      case "cancelled":
      case "canceled":
      case "no_show":
        return <CancelIcon className="w-3 h-3" />;
      default:
        return <Calendar className="w-3 h-3" />;
    }
  };

  const canCheckInNow = () => {
    if (!canCheckIn(booking)) return false;

    // Validate time window
    const now = new Date();
    const start = new Date(booking.startTime);
    const end = new Date(booking.endTime);
    return now >= start && now <= end;
  };

  const handleAction = async (action, actionName) => {
    if (isProcessing) return;

    setIsProcessing(true);
    onClose();

    try {
      await action();
    } catch (error) {
      logger.error(`Error performing ${actionName}:`, error);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = date => {
    const validDate = date instanceof Date ? date : new Date(date);
    if (isNaN(validDate.getTime())) return "Invalid Time";

    return validDate.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = date => {
    const validDate = date instanceof Date ? date : new Date(date);
    if (isNaN(validDate.getTime())) return "Invalid Date";

    return validDate.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  };

  if (!isOpen || !booking) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-full max-h-[80vh] overflow-y-auto p-0">
        <div className="p-3">
          {/* Header - Ultra Compact */}
          <DialogHeader className="mb-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <DialogTitle className="text-base font-semibold text-gray-900">Booking Details</DialogTitle>
                <DialogDescription className="text-xs text-gray-500">Manage reservation</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Space Image - Ultra Compact */}
          {booking.image && (
            <div className="mb-3 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
              <div className="relative w-full h-16 bg-gray-100">
                <img
                  src={booking.image}
                  alt="Space"
                  className="object-cover w-full h-full"
                  onError={e => {
                    // Prevent infinite loading loop
                    e.target.onerror = null;
                    e.target.style.display = "none";
                    // Show fallback icon
                    const fallback = document.createElement("div");
                    fallback.className =
                      "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100";
                    fallback.innerHTML =
                      '<div class="text-blue-400"><svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg></div>';
                    e.target.parentElement.appendChild(fallback);
                  }}
                />
                <div className="absolute inset-0 bg-black/20"></div>
              </div>
            </div>
          )}

          {/* Booking Info - Ultra Compact */}
          <div className="mb-3">
            <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
              <div className="overflow-hidden flex-shrink-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <img
                  src={booking.avatar}
                  alt={booking.name}
                  className="object-cover w-full h-full rounded-full"
                  onError={e => {
                    e.target.onerror = null;
                    e.target.style.display = "none";
                    e.target.parentNode.innerHTML =
                      '<div class="flex justify-center items-center w-full h-full text-xs font-medium text-white">' +
                      booking.name
                        .split(" ")
                        .map(n => n[0])
                        .join("") +
                      "</div>";
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-xs truncate">{booking.name}</div>
                <div className="text-xs text-gray-600 truncate">{booking.email}</div>
                <div className="flex items-center mt-0.5">
                  <div
                    className={`px-1.5 py-0.5 text-xs rounded-full flex items-center space-x-1 font-medium ${getStatusColor()}`}
                  >
                    {getStatusIcon()}
                    <span className="capitalize">{getBookingStatus().replace("_", " ")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Date and Time - Ultra Compact */}
          <div className="mb-3">
            <div className="flex items-center space-x-2 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center">
                <Calendar className="w-3 h-3 text-white" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-900 text-xs">{formatDate(booking.startTime)}</div>
                <div className="text-xs text-indigo-700 flex items-center font-medium">
                  <Clock className="w-3 h-3 mr-1" />
                  {formatTimeRange(booking.startTime, booking.endTime)}
                </div>
              </div>
            </div>
          </div>

          {/* Attendees */}
          {booking.emails && booking.emails.length > 0 && (
            <div className="mb-3">
              <div className="flex flex-col p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center space-x-2 mb-1">
                  <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                    <Users className="w-3 h-3 text-gray-600" />
                  </div>
                  <div className="text-xs font-semibold text-gray-900">Attendees</div>
                </div>
                <div className="flex flex-wrap gap-1 pl-8">
                  {booking.emails.map((email, idx) => (
                    <div
                      key={idx}
                      className="text-[10px] bg-white border border-gray-200 px-2 py-0.5 rounded-full text-gray-600 truncate block max-w-full"
                    >
                      {email}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Addons Breakdown */}
          {booking.addons && booking.addons.length > 0 && (
            <div className="mb-3">
              <AddonsBreakdown addons={booking.addons} currency={booking.currency || "GHS"} />
            </div>
          )}

          {/* Location Info - Ultra Compact */}
          {booking.location && (
            <div className="mb-3">
              <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                  <MapPin className="w-3 h-3 text-gray-600" />
                </div>
                <div className="flex-1">
                  <div className="text-xs font-semibold text-gray-900">Location</div>
                  <div className="text-xs text-gray-600">{booking.location}</div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons - Ultra Compact */}
          <div className="space-y-1.5">
            {canModify(booking) && (
              <button
                onClick={() => handleAction(() => onModify(booking), "modify")}
                disabled={isProcessing}
                className="w-full flex items-center justify-between p-2 border-2 border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-all disabled:opacity-50 group"
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center mr-2 group-hover:bg-indigo-200 transition-colors">
                    <Edit3 className="w-3 h-3 text-indigo-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-xs">Modify Booking</div>
                    <div className="text-xs text-gray-600">Change time, duration, or details</div>
                  </div>
                </div>
                <div className="text-gray-400 group-hover:text-indigo-500 transition-colors">→</div>
              </button>
            )}

            {canCheckIn(booking) && (
              <button
                onClick={() => {
                  const now = new Date();
                  const start = new Date(booking.startTime);
                  const end = new Date(booking.endTime);

                  if (now < start) return; // Button should be disabled anyway
                  if (now > end) {
                    showApiMessage(
                      { status: 400, message: "Cannot check in. The booking has already ended." },
                      "check in"
                    );
                    return;
                  }

                  onClose();
                  showConfirmation("check-in", booking);
                }}
                disabled={isProcessing || new Date() < new Date(booking.startTime)}
                className="w-full flex items-center justify-between p-2 border-2 border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all disabled:opacity-50 disabled:hover:border-gray-200 disabled:hover:bg-white group"
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2 group-hover:bg-green-200 transition-colors">
                    <LogIn className="w-3 h-3 text-green-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-xs flex items-center gap-2">
                      Check In
                      {new Date() < new Date(booking.startTime) && (
                        <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          Starting soon
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600">
                      {new Date() < new Date(booking.startTime)
                        ? `Available at ${formatTime(new Date(booking.startTime))}`
                        : "Mark guest as arrived"}
                    </div>
                  </div>
                </div>
                {new Date() >= new Date(booking.startTime) && (
                  <div className="text-gray-400 group-hover:text-green-500 transition-colors">→</div>
                )}
              </button>
            )}

            {canCheckOut(booking) && (
              <button
                onClick={() => {
                  onClose();
                  showConfirmation("check-out", booking);
                }}
                disabled={isProcessing}
                className="w-full flex items-center justify-between p-2 border-2 border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all disabled:opacity-50 group"
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 group-hover:bg-blue-200 transition-colors">
                    <LogOut className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-xs">Check Out</div>
                    <div className="text-xs text-gray-600">Mark guest as departed</div>
                  </div>
                </div>
                <div className="text-gray-400 group-hover:text-blue-500 transition-colors">→</div>
              </button>
            )}

            {canCancel(booking) && (
              <button
                onClick={() => {
                  onClose();
                  showConfirmation("cancel", booking);
                }}
                disabled={isProcessing}
                className="w-full flex items-center justify-between p-2 border-2 border-gray-200 rounded-lg hover:border-red-300 hover:bg-red-50 transition-all disabled:opacity-50 group"
              >
                <div className="flex items-center">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mr-2 group-hover:bg-red-200 transition-colors">
                    <CancelIcon className="w-3 h-3 text-red-600" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 text-xs">Cancel Booking</div>
                    <div className="text-xs text-gray-600">Cancel this reservation</div>
                  </div>
                </div>
                <div className="text-gray-400 group-hover:text-red-500 transition-colors">→</div>
              </button>
            )}
          </div>

          {/* Footer - Ultra Compact */}
          <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium text-xs"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BookingActionsModal;
